import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { API_URL, adminFetch } from './shared';

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(7);
  const [products, setProducts] = useState({});

  const fetchData = async (days) => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/analytics?days=${days}`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('analytics error', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(range); }, [range]);

  useEffect(() => {
    // Fetch product names once for top_products display
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (res.ok) {
          const list = await res.json();
          const map = {};
          list.forEach(p => { map[p.product_id] = p.name?.es || p.name?.en || `#${p.product_id}`; });
          setProducts(map);
        }
      } catch (e) { console.error('Analytics product names fetch failed:', e); }
    })();
  }, []);

  if (loading && !data) {
    return <div className="text-center py-8 text-slate-500 text-sm">Cargando métricas…</div>;
  }
  if (!data) {
    return <div className="text-center py-8 text-slate-500 text-sm">Sin datos aún — las métricas empezarán a poblarse cuando visiten tu tienda.</div>;
  }

  const totals = data.totals || {};
  const today = data.today || {};
  const daily = data.daily || [];
  const topProducts = data.top_products || [];
  const shareChannels = data.shares_by_channel || [];

  // Compact SVG line/bar chart (no extra deps to keep bundle small)
  const chartData = daily.map(d => ({ day: d.day.slice(5), views: d.page_view, productViews: d.product_view, shares: d.share }));
  const maxVal = Math.max(1, ...chartData.map(d => Math.max(d.views, d.productViews)));

  return (
    <Card className="bg-white p-4" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-600" />
          Métricas de la Tienda
        </h3>
        <div className="flex items-center gap-1.5">
          {[7, 30].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${range === d ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`analytics-range-${d}`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => fetchData(range)}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-500 ml-1"
            title="Actualizar"
            data-testid="analytics-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <MetricCard label="Visitas" value={totals.page_views} sub={`Hoy: ${today.page_views}`} icon="👁️" color="cyan" />
        <MetricCard label="Visitantes únicos" value={totals.unique_visitors} sub={`${range}d`} icon="👤" color="indigo" />
        <MetricCard label="Vistas de producto" value={totals.product_views} sub={`Hoy: ${today.product_views}`} icon="🛍️" color="amber" />
        <MetricCard label="Shares" value={totals.shares} sub={`Hoy: ${today.shares}`} icon="🔗" color="emerald" />
        <MetricCard label="Add to Cart" value={totals.add_to_cart} sub={`${range}d`} icon="🛒" color="pink" />
        <MetricCard label="Iniciaron Checkout" value={totals.checkout_start} sub={`${range}d`} icon="💳" color="purple" />
        <MetricCard label="Pedidos" value={totals.orders} sub={`${range}d`} icon="📦" color="slate" />
        <MetricCard label="Conversión" value={`${totals.conversion_rate ?? 0}%`} sub="pedidos/visitas" icon="🎯" color="green" />
      </div>

      {/* Time series chart */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">Actividad diaria ({range}d)</p>
        <div className="bg-slate-50 rounded-lg p-3 overflow-x-auto">
          <div className="flex items-end gap-1 min-w-full h-28" data-testid="analytics-chart">
            {chartData.map((d, i) => {
              const h1 = Math.round((d.views / maxVal) * 100);
              const h2 = Math.round((d.productViews / maxVal) * 100);
              return (
                <div key={d.day} className="flex-1 min-w-[10px] flex flex-col items-center gap-0.5 group relative">
                  <div className="w-full flex items-end gap-0.5 h-full">
                    <div className="flex-1 bg-cyan-400 hover:bg-cyan-500 rounded-t transition-all" style={{ height: `${h1}%`, minHeight: d.views > 0 ? '2px' : '0' }} />
                    <div className="flex-1 bg-amber-400 hover:bg-amber-500 rounded-t transition-all" style={{ height: `${h2}%`, minHeight: d.productViews > 0 ? '2px' : '0' }} />
                  </div>
                  <span className="text-[8px] text-slate-400 mt-0.5">{d.day}</span>
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    {d.views} vis · {d.productViews} prod · {d.shares} shr
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-cyan-400 rounded-sm" /> Visitas</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-sm" /> Vistas de producto</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Top products */}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-slate-600 mb-2">Productos más vistos</p>
          {topProducts.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">Sin datos todavía</p>
          ) : (
            <ol className="space-y-1.5">
              {topProducts.map((p, i) => {
                const maxV = topProducts[0]?.views || 1;
                const w = Math.round((p.views / maxV) * 100);
                return (
                  <li key={p.product_id} className="flex items-center gap-2 text-[11px]" data-testid={`top-product-${p.product_id}`}>
                    <span className="w-4 text-slate-400 font-bold">{i + 1}.</span>
                    <span className="flex-1 truncate text-slate-700">{products[p.product_id] || `Producto #${p.product_id}`}</span>
                    <div className="w-16 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-cyan-500 h-full" style={{ width: `${w}%` }} />
                    </div>
                    <span className="w-8 text-right font-bold text-slate-700">{p.views}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Share breakdown */}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-slate-600 mb-2">Compartidos por canal</p>
          {shareChannels.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">Sin shares todavía</p>
          ) : (
            <ul className="space-y-1.5">
              {shareChannels.map(s => {
                const total = shareChannels.reduce((a, b) => a + b.count, 0);
                const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                const label = s.channel === 'whatsapp' ? '📱 WhatsApp' : s.channel === 'instagram' ? '📸 Instagram' : s.channel === 'copy' ? '🔗 Copy Link' : s.channel;
                return (
                  <li key={s.channel} className="flex items-center gap-2 text-[11px]" data-testid={`share-channel-${s.channel}`}>
                    <span className="flex-1 text-slate-700">{label}</span>
                    <div className="w-20 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-emerald-500 h-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-8 text-right font-bold text-slate-700">{s.count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
};

const MetricCard = ({ label, value, sub, icon, color }) => {
  const colorMap = {
    cyan: 'bg-cyan-50 text-cyan-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    pink: 'bg-pink-50 text-pink-700',
    purple: 'bg-purple-50 text-purple-700',
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-green-50 text-green-700',
  };
  return (
    <div className={`rounded-lg p-2.5 ${colorMap[color] || 'bg-slate-50 text-slate-700'}`}>
      <div className="flex items-center gap-1 text-[10px] font-semibold opacity-80">
        <span>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <p className="text-lg font-black leading-tight mt-1">{value ?? 0}</p>
      {sub && <p className="text-[9px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
};
